import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Close, NoteAdd, UploadFile } from '@mui/icons-material';
import {
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';

import {
	PrimaryButton,
	RED_COLOR_SX,
} from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IPatientAssetUploadRequest,
	ISelectedUploadItem,
	IUploadDialogProps,
	PATIENT_ASSET_LOADING_KEY,
	PatientAssetType,
} from 'src/features/patient_asset';
import { uploadPatientAssets } from 'src/features/patient_asset/store/patientAssetThunks';
import {
	convertDicomToPngFile,
	isDicomFile,
	isImageMimeType,
} from 'src/features/patient_asset/utils/dicomToImage';
import { AppDispatch } from 'src/store';
import { snakeToTitleCase } from 'src/utils/formatter';

import FilePreview from './FilePreview';
import SelectedImagesPreview from './SelectedImagesPreview';

const MAX_FILE_SIZE = Number(
	process.env.REACT_APP_MAX_FILE_UPLOAD_SIZE || 83886080,
);

const createLocalId = (): string =>
	`${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const PatientAssetUploadModal: React.FC<IUploadDialogProps> = ({
	isOpen,
	closeUploadDialog,
	onUploadSuccess,
	question,
}) => {
	const { patientId, studyId, siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector((state: { loading: ILoadingState }) => {
		return state.loading.loadingStates[PATIENT_ASSET_LOADING_KEY];
	});

	// PDF (file) fields: single selection
	const [file, setFile] = useState<File | null>(null);
	// Image fields: multi selection
	const [selectedItems, setSelectedItems] = useState<ISelectedUploadItem[]>(
		[],
	);
	const [isConverting, setIsConverting] = useState(false);
	const [conversionProgress, setConversionProgress] = useState<{
		current: number;
		total: number;
	} | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const conversionRequestIdRef = useRef(0);

	const fileType = question.type as PatientAssetType;
	const isImageField = fileType === 'image';

	const validInputTypes = useMemo(() => {
		switch (fileType) {
			case 'image':
				// */* allows extensionless DICOM that OS pickers would otherwise hide
				return 'image/jpeg,image/jpg,image/png,.dcm,.dicom,application/dicom,application/octet-stream,*/*';
			case 'file':
				return 'application/pdf';
			default:
				return '';
		}
	}, [fileType]);

	const inputId = `upload-input-${question.id}`;

	const isWithinSizeLimit = (candidate: File): boolean =>
		candidate.size <= MAX_FILE_SIZE;

	const resetFileSelection = () => {
		setFile(null);
		setSelectedItems([]);
		setIsConverting(false);
		setConversionProgress(null);
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const clearInputValue = () => {
		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const prepareImageFile = async (
		candidate: File,
	): Promise<ISelectedUploadItem | null> => {
		if (isImageMimeType(candidate)) {
			if (!isWithinSizeLimit(candidate)) {
				return null;
			}
			return {
				id: createLocalId(),
				file: candidate,
				dicomConversion: null,
			};
		}

		const dicom = await isDicomFile(candidate);
		if (!dicom) {
			return null;
		}

		const result = await convertDicomToPngFile(candidate);
		if (!isWithinSizeLimit(result.file)) {
			return null;
		}

		return {
			id: createLocalId(),
			file: result.file,
			dicomConversion: {
				sourceName: result.sourceName,
				width: result.width,
				height: result.height,
			},
		};
	};

	const handlePdfFileChange = (fileToUpload: File) => {
		const validTypes = validInputTypes.split(',');
		if (
			!validTypes.includes(fileToUpload.type) ||
			!isWithinSizeLimit(fileToUpload)
		) {
			snackbar(
				`Invalid file. Make sure it's a valid ${fileType} and under 80MB.`,
				'error',
			);
			resetFileSelection();
			return;
		}
		setSelectedItems([]);
		setFile(fileToUpload);
	};

	const handleImageFilesChange = async (files: File[]) => {
		const requestId = ++conversionRequestIdRef.current;
		const prepared: ISelectedUploadItem[] = [];
		let skipped = 0;
		let dicomCount = 0;

		// Pre-check how many look like DICOM for progress messaging
		for (const candidate of files) {
			if (!isImageMimeType(candidate)) {
				dicomCount += 1;
			}
		}

		const needsConversion = dicomCount > 0;
		if (needsConversion) {
			setIsConverting(true);
			setConversionProgress({ current: 0, total: files.length });
		}

		try {
			let processed = 0;
			for (const candidate of files) {
				if (requestId !== conversionRequestIdRef.current) return;

				processed += 1;
				if (needsConversion) {
					setConversionProgress({
						current: processed,
						total: files.length,
					});
				}

				try {
					const item = await prepareImageFile(candidate);
					if (item) {
						prepared.push(item);
					} else {
						skipped += 1;
					}
				} catch (error) {
					console.error(error);
					skipped += 1;
				}
			}

			if (requestId !== conversionRequestIdRef.current) return;

			if (prepared.length > 0) {
				setSelectedItems((prev) => [...prev, ...prepared]);
			}

			if (skipped > 0 && prepared.length > 0) {
				snackbar(
					`${prepared.length} file${prepared.length === 1 ? '' : 's'} added. ${skipped} skipped (invalid or over 80MB).`,
					'warning',
				);
			} else if (skipped > 0 && prepared.length === 0) {
				snackbar(
					`Invalid file${skipped === 1 ? '' : 's'}. Make sure ${skipped === 1 ? "it's" : "they're"} JPEG, PNG, or DICOM and under 80MB.`,
					'error',
				);
			}
		} finally {
			if (requestId === conversionRequestIdRef.current) {
				setIsConverting(false);
				setConversionProgress(null);
				clearInputValue();
			}
		}
	};

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const selectedFiles = event.target.files;
		if (!selectedFiles || selectedFiles.length === 0) return;

		if (fileType === 'file') {
			handlePdfFileChange(selectedFiles[0]);
			return;
		}

		await handleImageFilesChange(Array.from(selectedFiles));
	};

	const handleRemoveSelectedItem = (id: string) => {
		setSelectedItems((prev) => prev.filter((item) => item.id !== id));
	};

	const formatRequestData = (): IPatientAssetUploadRequest[] | null => {
		if (!patientId || !studyId) return null;

		if (isImageField) {
			if (selectedItems.length === 0) return null;
			return selectedItems.map((item, index) => ({
				patient_id: Number(patientId),
				study_id: Number(studyId),
				file: item.file,
				source: 'questionnaire' as const,
				questionnaire_field_id: question.id,
				type: fileType,
				file_key: `file_${index + 1}`,
			}));
		}

		if (!file) return null;
		return [
			{
				patient_id: Number(patientId),
				study_id: Number(studyId),
				file,
				source: 'questionnaire',
				questionnaire_field_id: question.id,
				type: fileType,
				file_key: 'file_1',
			},
		];
	};

	const handleClose = () => {
		if (isLoading || isConverting) return;
		conversionRequestIdRef.current += 1;
		resetFileSelection();
		closeUploadDialog();
	};

	const hasSelection = isImageField
		? selectedItems.length > 0
		: file !== null;

	const handleSave = async () => {
		if (!siteSlug) return;

		if (isConverting) {
			snackbar('Please wait for DICOM conversion to finish.', 'error');
			return;
		}

		if (!hasSelection) {
			snackbar(
				isImageField
					? 'Please select at least one image to upload.'
					: `Please select a ${fileType} to upload.`,
				'error',
			);
			return;
		}

		const requestData = formatRequestData();
		if (!requestData) return;

		const uploadCount = requestData.length;

		dispatch(
			uploadPatientAssets({
				request: requestData,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					const formattedAssetData = response.flatMap((item) =>
						item.uploaded_assets.map((asset) => {
							const formattedData = {
								id: asset.id,
								url: asset.url,
								thumbnail_url: asset.thumbnail_url,
							};

							return formattedData;
						}),
					);
					onUploadSuccess(formattedAssetData);
					closeUploadDialog();

					resetFileSelection();

					const label = snakeToTitleCase(fileType);
					snackbar(
						uploadCount === 1
							? `${label} uploaded successfully.`
							: `${uploadCount} ${label.toLowerCase()}s uploaded successfully.`,
						'success',
					);
				},
			}),
		);
	};

	if (!isOpen) return null;

	const isBusy = isLoading || isConverting;
	const dialogHeight =
		isImageField && selectedItems.length > 4 ? '560px' : '506px';

	const openFilePicker = () => {
		document.getElementById(inputId)?.click();
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				maxWidth='sm'
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: '1rem',
						width: '600px',
						height: dialogHeight,
					},
				}}
			>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						paddingBottom: '0px',
					}}
				>
					<Typography fontSize={'20px'} textTransform={'capitalize'}>
						Upload {snakeToTitleCase(fileType)}
					</Typography>

					<IconButton onClick={handleClose} disabled={isBusy}>
						<Close
							sx={{
								cursor: 'pointer',
								width: '24px',
								height: '24px',

								color: isBusy ? 'rgba(0,0,0,.6)' : 'black',
							}}
						/>
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							paddingTop: '10px',
						}}
					>
						<Box
							sx={{
								width: 'full',
								height: '370px',
							}}
						>
							{isConverting ? (
								<Box
									sx={{
										border: 1,
										borderStyle: 'dashed',
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 1.5,
									}}
								>
									<CircularProgress size={36} />
									<Typography color='text.secondary'>
										{conversionProgress
											? `Converting DICOM (${conversionProgress.current}/${conversionProgress.total})…`
											: 'Converting DICOM…'}
									</Typography>
								</Box>
							) : isImageField && selectedItems.length > 0 ? (
								<SelectedImagesPreview
									items={selectedItems}
									onRemove={handleRemoveSelectedItem}
									disabled={isBusy}
								/>
							) : file && !isImageField ? (
								<FilePreview file={file} type={fileType} />
							) : (
								<Box
									sx={{
										border: 1,
										borderStyle: 'dashed',
										width: '100%',
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Box>
										<Box
											sx={{
												cursor: 'pointer',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												px: 2,
											}}
											onClick={openFilePicker}
										>
											<NoteAdd
												sx={{
													color: '#2196F3',
												}}
											/>
											<Typography
												sx={{
													textDecorationLine:
														'underline',
													color: '#2196F3',
												}}
											>
												{isImageField
													? 'Click to upload images'
													: `Click to upload the ${fileType.toLowerCase()}`}
											</Typography>
											{isImageField && (
												<Typography
													variant='caption'
													sx={{
														mt: 1,
														color: 'text.secondary',
														textAlign: 'center',
														maxWidth: 360,
													}}
												>
													JPEG, PNG, or DICOM (.dcm or
													no extension). Select
													multiple files at once.
													DICOM files are converted to
													PNG before upload (original
													resolution preserved; first
													frame only for multi-frame).
												</Typography>
											)}
										</Box>
									</Box>
								</Box>
							)}
						</Box>

						<DialogActions
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								padding: '0px',
							}}
						>
							{hasSelection && !isConverting && (
								<>
									<Box
										onClick={
											isBusy ? undefined : openFilePicker
										}
										sx={{
											cursor: isBusy
												? 'not-allowed'
												: 'pointer',
											userSelect: 'none',
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											opacity: isBusy ? 0.5 : 1,
											pointerEvents: isBusy
												? 'none'
												: 'auto',
										}}
									>
										<Typography
											sx={{
												textDecorationLine: 'underline',
												color: '#00000099',
											}}
										>
											{isImageField
												? 'Add more'
												: `Replace ${snakeToTitleCase(fileType)}`}
										</Typography>
										<UploadFile
											sx={{ color: '#00000099' }}
										/>
									</Box>
								</>
							)}

							<PrimaryButton
								fullWidth={false}
								onClick={handleSave}
								isLoading={isLoading}
								disabled={isBusy || !hasSelection}
								sx={{ ...RED_COLOR_SX, ml: 'auto' }}
							>
								{isLoading
									? 'Uploading'
									: isConverting
										? 'Converting'
										: 'Upload'}
							</PrimaryButton>
						</DialogActions>
					</Box>
				</DialogContent>
				<input
					ref={inputRef}
					type='file'
					id={inputId}
					accept={validInputTypes}
					multiple={isImageField}
					onChange={handleFileChange}
					hidden
					disabled={isBusy}
				/>
			</Dialog>
		</>
	);
};

export default PatientAssetUploadModal;
